import React, { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { PATHS } from 'router/config';
import { MoneyCell } from 'molecules';
import { useStudentDeepLinkState } from 'core/hooks';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../StudentDetail.styled';
import {
  IAgreement,
  IPayment,
  IStudent,
  PickContributionForPaymentPopup,
} from '../parts';

interface IPaymentsTabProps {
  student: IStudent;
  onChanged?: () => void;
}

type RowStatus = 'paid' | 'partial' | 'overdue' | 'upcoming';
type StatusFilter = 'all' | 'unpaid' | 'paid';

interface IRow {
  rowId: string;
  status: RowStatus;
  date: string;
  amount: number;
  currency: string;
  agreementId: string;
  agreementTitle: string;
  groupName?: string;
  delayDays?: number;
  remainder?: number;
  paymentDate?: string;
  paymentAmount?: number;
  invoiceId?: string;
  invoiceNumber?: string;
}

interface IMigrationRow {
  payment: IPayment;
  agreement: IAgreement;
}

const STATUS_TONE: Record<RowStatus, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  paid: 'ok',
  partial: 'warn',
  overdue: 'danger',
  upcoming: 'neutral',
};

/**
 * Cross-agreement schedule view. One row per `Contribution`. Orphan
 * Payments — should not appear in the new model — surface in a separate
 * «Требуют привязки» mini-block at the top so the operator can wire
 * them to the schedule with one click.
 *
 * The stream is sorted by date desc so the most recent activity floats
 * up. Filters: agreement, status (all / unpaid / paid).
 */
export const PaymentsTab = memo(
  forwardRef<HTMLDivElement, IPaymentsTabProps>(
    ({ student, onChanged }, needsLinkingRef) => {
      const { t } = useTranslation();
      const history = useHistory();
      // Deep-linked filters (Req 20.2/20.3): read + write through the URL hook
      // so the agreement/status filters survive a reload and are restored from
      // an alert navigation. `setPayments*` no-op when the value is unchanged.
      const {
        state: deepLink,
        setPaymentsAgreement,
        setPaymentsStatus,
      } = useStudentDeepLinkState();
      const agreementFilter = deepLink.paymentsAgreement;
      const statusFilter = deepLink.paymentsStatus;
      const setAgreementFilter = setPaymentsAgreement;
      const setStatusFilter = setPaymentsStatus;
      const [migrating, setMigrating] = useState<IMigrationRow | null>(null);

      const rows: IRow[] = useMemo(() => {
        const out: IRow[] = [];
        (student.agreements || []).forEach((a) => {
          if (!a) return;
          const currency = a.currency || '';
          (a.contributions?.edges || []).forEach((edge) => {
            const c = edge?.node;
            if (!c) return;
            const remainder = c.remainder ?? 0;
            const status: RowStatus = c.complete
              ? remainder > 0
                ? 'partial'
                : 'paid'
              : (c.delay ?? 0) > 0
              ? 'overdue'
              : 'upcoming';
            const inv = c.payment?.fakturowniaInvoice;
            out.push({
              rowId: `c:${c.id}`,
              status,
              date: c.date,
              amount: c.amountForeign || 0,
              currency,
              agreementId: a.id,
              agreementTitle: a.agreementTitle,
              groupName: a.group?.name,
              delayDays: c.delay ?? undefined,
              remainder: remainder || undefined,
              paymentDate: c.payment?.date,
              paymentAmount: c.payment?.amountAgreement || undefined,
              invoiceId: inv?.id,
              invoiceNumber:
                inv?.number || (inv?.pk ? `#${inv.pk}` : undefined),
            });
          });
        });
        return out.sort((x, y) => (x.date < y.date ? 1 : -1));
      }, [student.agreements]);

      const migrationRows: IMigrationRow[] = useMemo(() => {
        const out: IMigrationRow[] = [];
        (student.agreements || []).forEach((a) => {
          if (!a) return;
          (a.payments || []).forEach((p) => {
            if (p && !p.contribution) out.push({ payment: p, agreement: a });
          });
        });
        return out.sort((x, y) => (x.payment.date < y.payment.date ? 1 : -1));
      }, [student.agreements]);

      const filtered = useMemo(
        () =>
          rows.filter((r) => {
            if (agreementFilter !== 'all' && r.agreementId !== agreementFilter)
              return false;
            if (statusFilter === 'unpaid')
              return r.status === 'overdue' || r.status === 'upcoming';
            if (statusFilter === 'paid')
              return r.status === 'paid' || r.status === 'partial';
            return true;
          }),
        [rows, agreementFilter, statusFilter],
      );

      const filteredMigration = useMemo(
        () =>
          agreementFilter === 'all'
            ? migrationRows
            : migrationRows.filter((m) => m.agreement.id === agreementFilter),
        [migrationRows, agreementFilter],
      );

      const agreementOptions = useMemo(
        () =>
          (student.agreements || [])
            .filter((a): a is NonNullable<typeof a> => Boolean(a))
            .map((a) => ({ id: a.id, label: a.agreementTitle })),
        [student.agreements],
      );

      const openInvoice = useCallback(
        (invoiceId: string) =>
          history.push(PATHS.financesInvoice.replace(':id', invoiceId)),
        [history],
      );

      return (
        <div style={{ marginTop: 16 }} ref={needsLinkingRef}>
          <S.FilterBar>
            <S.FilterGroup>
              <S.FilterLabel>
                {t('pages.studentDetail.payments.filters.agreement')}
              </S.FilterLabel>
              <S.NativeSelect
                value={agreementFilter}
                onChange={(e) => setAgreementFilter(e.target.value)}
              >
                <option value="all">
                  {t('pages.studentDetail.payments.filters.allAgreements')}
                </option>
                {agreementOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </S.NativeSelect>
            </S.FilterGroup>

            <S.FilterGroup>
              <S.FilterLabel>
                {t('pages.studentDetail.payments.filters.status')}
              </S.FilterLabel>
              <S.SegmentTabs>
                {(['all', 'unpaid', 'paid'] as StatusFilter[]).map((v) => (
                  <S.SegmentTab
                    key={v}
                    type="button"
                    active={statusFilter === v}
                    onClick={() => setStatusFilter(v)}
                  >
                    {t(`pages.studentDetail.payments.filters.status_${v}`)}
                  </S.SegmentTab>
                ))}
              </S.SegmentTabs>
            </S.FilterGroup>
          </S.FilterBar>

          {filteredMigration.length > 0 ? (
            <S.Card>
              <L.SubblockHeader>
                {t('pages.studentDetail.payments.migration.title')}
                <L.SubblockHeaderCount>
                  · {filteredMigration.length}
                </L.SubblockHeaderCount>
              </L.SubblockHeader>
              <L.Muted style={{ marginBottom: 8 }}>
                {t('pages.studentDetail.payments.migration.hint')}
              </L.Muted>
              <L.SmallTable>
                <thead>
                  <tr>
                    <th>{t('pages.studentDetail.payments.date')}</th>
                    <th style={{ textAlign: 'right' }}>
                      {t('pages.studentDetail.payments.amount')}
                    </th>
                    <th>{t('pages.studentDetail.payments.agreementCol')}</th>
                    <th aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {filteredMigration.map(({ payment, agreement }) => (
                    <L.OrphanRowTone key={payment.id}>
                      <td>{moment(payment.date).format('DD.MM.YYYY')}</td>
                      <td style={{ textAlign: 'right' }}>
                        <MoneyCell
                          amount={payment.amountAgreement}
                          currency={
                            payment.currency || agreement.currency || ''
                          }
                        />
                      </td>
                      <td>{agreement.agreementTitle}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <L.PrimaryActionButton
                          type="button"
                          tone="primary"
                          onClick={() => setMigrating({ payment, agreement })}
                        >
                          {t(
                            'pages.studentDetail.payments.migration.linkAction',
                          )}
                        </L.PrimaryActionButton>
                      </td>
                    </L.OrphanRowTone>
                  ))}
                </tbody>
              </L.SmallTable>
            </S.Card>
          ) : null}

          {filtered.length === 0 ? (
            <S.EmptyState>
              {t('pages.studentDetail.payments.empty')}
            </S.EmptyState>
          ) : (
            <S.Card>
              <L.SmallTable>
                <thead>
                  <tr>
                    <th>{t('pages.studentDetail.payments.date')}</th>
                    <th style={{ textAlign: 'right' }}>
                      {t('pages.studentDetail.payments.amount')}
                    </th>
                    <th>{t('pages.studentDetail.payments.filters.status')}</th>
                    <th>{t('pages.studentDetail.payments.agreementCol')}</th>
                    <th>{t('pages.studentDetail.payments.groupCol')}</th>
                    <th>
                      {t('pages.studentDetail.payments.schedule.colPayment')}
                    </th>
                    <th>{t('pages.studentDetail.payments.linkedInvoice')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.rowId}>
                      <td>{moment(r.date).format('DD.MM.YYYY')}</td>
                      <td style={{ textAlign: 'right' }}>
                        <MoneyCell amount={r.amount} currency={r.currency} />
                      </td>
                      <td>
                        <L.Tag tone={STATUS_TONE[r.status]}>
                          {t(
                            `pages.studentDetail.payments.schedule.status.${r.status}`,
                          )}
                        </L.Tag>
                        {r.status === 'overdue' && r.delayDays ? (
                          <L.Muted style={{ marginLeft: 6 }}>
                            {t(
                              'pages.studentDetail.payments.schedule.delayDays',
                              {
                                count: r.delayDays,
                              },
                            )}
                          </L.Muted>
                        ) : null}
                      </td>
                      <td>{r.agreementTitle}</td>
                      <td>{r.groupName || '—'}</td>
                      <td>
                        {r.paymentDate ? (
                          <span>
                            {moment(r.paymentDate).format('DD.MM.YYYY')}{' '}
                            <L.Muted>
                              ·{' '}
                              {`${r.paymentAmount ?? '—'} ${r.currency}`.trim()}
                            </L.Muted>
                          </span>
                        ) : (
                          <L.Muted>—</L.Muted>
                        )}
                      </td>
                      <td>
                        {r.invoiceId ? (
                          <L.PaymentLink
                            href={PATHS.financesInvoice.replace(
                              ':id',
                              r.invoiceId,
                            )}
                            onClick={(e) => {
                              e.preventDefault();
                              openInvoice(r.invoiceId as string);
                            }}
                          >
                            {r.invoiceNumber}
                          </L.PaymentLink>
                        ) : (
                          <L.Muted>—</L.Muted>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </L.SmallTable>
            </S.Card>
          )}

          {migrating ? (
            <PickContributionForPaymentPopup
              payment={migrating.payment}
              agreement={migrating.agreement}
              studentId={student.id}
              onClose={() => setMigrating(null)}
              onLinked={onChanged}
            />
          ) : null}
        </div>
      );
    },
  ),
);

PaymentsTab.displayName = 'PaymentsTab';
